import React, { useState } from "react";
import { Modal, Form, Input, Icon, Avatar } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import countries from "i18n-iso-countries";
import { Link } from "react-router-dom";

import connection from "../../../assets/connection.svg";
import connected from "../../../assets/connected.svg";
import {
  connectCompanySocialAccount,
  CONNECT_COMPANY_SOCIAL_ACCOUNT
} from "../../../store/actions/company";
import { isLoading } from "../../../utils/actions";
import Button from "../../../components/Button";
import { httpToHttps } from "../../../utils/images";

import styled from "styled-components/macro";

const Title = styled.div`
  margin: 0px;
  font-size: 22px;
  margin-top: 10px;
  font-weight: bold;
  color: ${({ theme }) => theme["@gray-80"]};
  text-align: center;
`;

const Subtitle = styled.p`
  margin: 0px;
  font-size: 16px;
  color: ${({ theme }) => theme["@gray-80"]};
  text-align: center;
`;

const AvatarContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 40px;
  margin-bottom: 40px;
`;
const CustomAvatar = styled(Avatar)`
  border: 1px solid #e5e9ed;
`;

const ConnectionImg = styled.img`
  width: 64px;
  margin-left: 5px;
  margin-right: 5px;
`;

const Label = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme["@gray-60"]};
`;

const WrapperButton = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 40px;
  margin-bottom: 20px;
`;

const IntegrationModal = ({ form, visible = true, onClose, type }) => {
  const { t } = useTranslation();
  const { getFieldDecorator } = form;

  const dispatch = useDispatch();
  const loading = useSelector(state =>
    isLoading(CONNECT_COMPANY_SOCIAL_ACCOUNT, state)
  );

  const company = useSelector(({ company }) => company);
  const provider = `${type.name} ${
    type.country === "INT" ? "" : countries.getName(type.country, "es")
  }`.trim();
  const [step, setStep] = useState("init");

  const InfoAvatars = () => (
    <AvatarContainer>
      <CustomAvatar size={76} src={httpToHttps(company.logo)} />
      <ConnectionImg
        src={step === "init" ? connection : connected}
        alt="Link"
      />
      <CustomAvatar size={76} src={type.logo} />
    </AvatarContainer>
  );

  return (
    <Modal
      width={570}
      visible={visible}
      destroyOnClose={true}
      onCancel={() => !loading && onClose()}
      maskClosable={false}
      closable={!loading}
      keyboard={false}
      footer={null}
    >
      {step === "init" ? (
        <div>
          <Title>
            {`${t(
              "opportunity:wizard.multiposting.integrations.connect"
            )} ${provider}`}
          </Title>
          <InfoAvatars />
          <div style={{ marginBottom: 32 }}>
            <Subtitle>
              {t("opportunity:wizard.multiposting.integrations.credentials")}
            </Subtitle>
            <Subtitle>
              <strong>{provider}</strong>
            </Subtitle>
          </div>

          <Form
            layout="horizontal"
            style={{ width: 370, margin: "auto" }}
            onSubmit={ev => {
              ev.preventDefault();
              form.validateFields(async (err, values) => {
                if (!err) {
                  await dispatch(
                    connectCompanySocialAccount({
                      provider: type.provider,
                      country: type.country,
                      ...values
                    })
                  );
                  setStep("connected");
                }
              });
            }}
          >
            <Form.Item
              name="user"
              label={
                <Label>
                  {`${t(
                    "opportunity:wizard.multiposting.integrations.user"
                  )} ${provider}`}
                </Label>
              }
            >
              {getFieldDecorator("user", {
                rules: [
                  {
                    required: true,
                    message: t("form:emptyFieldValidation")
                  }
                ]
              })(
                <Input
                  prefix={
                    <Icon type="user" style={{ color: "rgba(0,0,0,.25)" }} />
                  }
                  data-test="integrations-user"
                />
              )}
            </Form.Item>
            <Form.Item
              name="password"
              label={
                <Label>
                  {t("opportunity:wizard.multiposting.integrations.password")}
                </Label>
              }
            >
              {getFieldDecorator("password", {
                rules: [
                  { required: true, message: t("form:emptyFieldValidation") }
                ]
              })(
                <Input
                  prefix={
                    <Icon type="lock" style={{ color: "rgba(0,0,0,.25)" }} />
                  }
                  type="password"
                  data-test="integrations-password"
                />
              )}
            </Form.Item>

            <WrapperButton>
              <Button
                type="quaternary"
                disabled={loading}
                onClick={onClose}
                size="lg"
              >
                {t("global:cancel")}
              </Button>
              <Button
                data-test="discard-button"
                loading={loading}
                htmlType="submit"
                type="primary"
                size="lg"
              >
                {t("global:connect")}
              </Button>
            </WrapperButton>
          </Form>
        </div>
      ) : (
        <div>
          <Title>
            {t("opportunity:wizard.multiposting.integrations.success")}
          </Title>
          <InfoAvatars />
          <div style={{ marginBottom: 32 }}>
            <Subtitle>
              {`${t(
                "opportunity:wizard.multiposting.integrations.successTitle"
              )} ${provider}`}
            </Subtitle>
          </div>
          <WrapperButton>
            <Button
              type="quaternary"
              disabled={loading}
              onClick={onClose}
              size="lg"
            >
              {t("global:close")}
            </Button>
            <Link to="/oportunidades">
              <Button data-test="discard-button" type="primary" size="lg">
                {t("global:viewOpps")}
              </Button>
            </Link>
          </WrapperButton>
        </div>
      )}
    </Modal>
  );
};

export default Form.create()(IntegrationModal);
